import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AlarmClock, Box, CalendarDays, Eye, Inbox, Keyboard, Layers, Moon, Palette, Plus, Settings, Star, Sun } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Kbd } from "@/components/ui/input";
import { PriorityIcon, ProjectGlyph, StatusIcon } from "@/components/icons";
import { useHierarchy, useSearch } from "@/data/hooks";
import { CONCEPT_ORDER, CONCEPTS } from "@/concepts";
import { useConcept } from "@/features/concept-provider";
import { openQuickAdd, openTask, ui, useUi } from "@/features/ui-state";
import { navigate, type Route } from "@/router";
import { stateLabel } from "@/features/screens/ListScreens";
import { SpaceIcon } from "@/components/container-icon";
import { shortcutKeys } from "@/features/keyboard/shortcuts";

interface Cmd {
  id: string;
  label: string;
  icon: ReactNode;
  keywords?: string;
  shortcut?: string;
  run: () => void;
}

function useDebounced<T>(v: T, ms: number) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const id = setTimeout(() => setD(v), ms);
    return () => clearTimeout(id);
  }, [v, ms]);
  return d;
}

export function CommandPalette() {
  const open = useUi((s) => s.paletteOpen);
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 120);
  const h = useHierarchy();
  const { concept, setConcept, dark, setAppearance } = useConcept();
  const { data, isFetching } = useSearch(open ? dq : "");
  const close = () => ui.set({ paletteOpen: false });
  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const go = (route: Route) => () => navigate(route);
  const commands: Cmd[] = [
    { id: "new", label: "New task", icon: <Plus />, shortcut: shortcutKeys("new"), keywords: "create add", run: () => openQuickAdd() },
    { id: "today", label: "Go to Today", icon: <Star />, keywords: "today", run: go({ name: "today" }) },
    { id: "inbox", label: "Go to Inbox", icon: <Inbox />, run: go({ name: "inbox" }) },
    { id: "upcoming", label: "Go to Upcoming", icon: <CalendarDays />, run: go({ name: "upcoming" }) },
    { id: "review", label: "Go to Review", icon: <Eye />, run: go({ name: "review" }) },
    { id: "snoozed", label: "Go to Snoozed", icon: <AlarmClock />, run: go({ name: "snoozed" }) },
    { id: "all", label: "Go to All tasks", icon: <Layers />, run: go({ name: "all" }) },
    { id: "settings", label: "Open Settings", icon: <Settings />, shortcut: shortcutKeys("settings"), run: go({ name: "settings" }) },
    {
      id: "shortcuts",
      label: "Keyboard shortcuts",
      icon: <Keyboard />,
      shortcut: shortcutKeys("shortcuts"),
      keywords: "keys hotkeys help reference",
      run: () => ui.set({ shortcutsOpen: true }),
    },
    {
      id: "appearance",
      label: dark ? "Switch to light appearance" : "Switch to dark appearance",
      icon: dark ? <Sun /> : <Moon />,
      keywords: "theme dark light mode toggle appearance",
      run: () => setAppearance(dark ? "light" : "dark"),
    },
  ];
  // Design concepts are a preview (Settings → Design concept); only offered when searched for.
  const conceptCommands: Cmd[] = CONCEPT_ORDER.filter((id) => id !== concept.id).map((id) => ({
    id: `concept-${id}`,
    label: `Preview ${CONCEPTS[id].name} concept`,
    icon: <Palette />,
    keywords: "concept design layout preview",
    run: () => setConcept(id),
  }));

  const needle = q.trim().toLowerCase();
  const match = (s: string) => !needle || s.toLowerCase().includes(needle);
  const cmds = [...commands, ...(needle ? conceptCommands : [])].filter((c) => match(`${c.label} ${c.keywords ?? ""}`));
  const containers = useMemo(() => {
    if (!needle) return [];
    return [
      ...h.areas.filter((a) => match(a.name)).map((a) => ({ id: a.id, label: a.name, icon: <Box />, route: { name: "area", id: a.id } as Route, sub: "Area" })),
      ...h.spaces
        .filter((s) => match(`${s.name} ${s.prefix}`))
        .map((s) => ({ id: s.id, label: s.name, icon: <SpaceIcon space={s} className="size-4 text-[9px]" />, route: { name: "space", id: s.id } as Route, sub: h.area(s.areaId)?.name })),
      ...h.projects
        .filter((p) => match(p.name))
        .map((p) => ({ id: p.id, label: p.name, icon: <ProjectGlyph state={p.state} />, route: { name: "project", id: p.id } as Route, sub: h.space(p.spaceId)?.name })),
    ].slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needle, h]);
  const tasks = needle ? (data?.tasks ?? []).slice(0, 30) : [];

  const run = (fn: () => void) => {
    close();
    fn();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => ui.set({ paletteOpen: o })}>
      <DialogContent position="top" hideClose className="max-w-[620px] overflow-hidden p-0">
        <DialogTitle className="sr-only">Command menu</DialogTitle>
        <Command shouldFilter={false} loop>
          <CommandInput value={q} onValueChange={setQ} placeholder="Search tasks, IDs, places, or commands…" autoFocus />
          <CommandList className="max-h-[56vh]">
            <CommandEmpty>{isFetching ? "Searching…" : "No results."}</CommandEmpty>
            {tasks.length > 0 && (
              <CommandGroup heading="Tasks">
                {tasks.map((t) => {
                  const state = stateLabel(t);
                  return (
                    <CommandItem key={t.id} value={`task-${t.id}`} onSelect={() => run(() => openTask(t.id, "overlay"))}>
                      <StatusIcon status={t.status} />
                      <span className="w-[60px] shrink-0 text-[12px] tabular text-subtle">{t.readableId}</span>
                      <span className="min-w-0 flex-1 truncate">{t.title}</span>
                      {state && <span className="shrink-0 rounded bg-muted px-1.5 text-[11.5px] text-muted-foreground">{state}</span>}
                      {t.priority !== "none" && <PriorityIcon priority={t.priority} className="!size-3.5" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
            {containers.length > 0 && (
              <CommandGroup heading="Places">
                {containers.map((c) => (
                  <CommandItem key={c.id} value={`place-${c.id}`} onSelect={() => run(() => navigate(c.route))}>
                    {c.icon}
                    <span className="truncate">{c.label}</span>
                    {c.sub && <span className="text-[12px] text-subtle">{c.sub}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {cmds.length > 0 && (
              <CommandGroup heading="Commands">
                {cmds.map((c) => (
                  <CommandItem key={c.id} value={`cmd-${c.id}`} onSelect={() => run(c.run)}>
                    {c.icon}
                    <span className="flex-1">{c.label}</span>
                    {c.shortcut && <Kbd>{c.shortcut}</Kbd>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
